import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { EmployerLayout } from '../JobPostPricing/EmployerLayout';
import { getApiUrlsWithFallback } from '../../../config/api';
import { Button } from '@/Components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/Components/ui/card';
import { Textarea } from '@/Components/ui/textarea';
import { Label } from '@/Components/ui/label';
import { Alert, AlertDescription } from '@/Components/ui/feedback';
import { Heading, Text } from '../../../Components/ui/typography';
import { Badge } from '@/Components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/Components/ui/collapsible';
import { Loader2, Lightbulb, Sparkles, ChevronDown, Copy, CheckCircle2, Zap, Briefcase, Users, DollarSign, MapPin, Clock, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getColor } from '@/config/theme';

function JdAi({ embedded = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [copiedExample, setCopiedExample] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(true);
  const textareaRef = useRef(null);

  useEffect(() => {
    // Auto-focus textarea on mount
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const examplePrompt = "We need a Senior Frontend Developer with 4-6 years experience in React and TypeScript. The role is remote-first with occasional office visits. Responsibilities include building responsive web applications, mentoring junior developers, and collaborating with UX designers. We're a fast-growing fintech startup with a collaborative culture. Budget is ₹10-15 lakhs.";

  const quickPrompts = [
    { 
      icon: Briefcase, 
      label: "Tech Role", 
      prompt: "We need a Senior Software Engineer with 5+ years experience in full-stack development, React, Node.js, and cloud technologies (AWS, Docker). The role is remote-first with flexible hours. Responsibilities include building scalable web applications, designing system architecture, code reviews, and mentoring junior developers. We're a fast-growing tech startup with a collaborative culture and focus on innovation. Budget is ₹12-18 lakhs." 
    },
    { 
      icon: Users, 
      label: "Management", 
      prompt: "We need a Product Manager with 4-6 years experience in agile methodologies, stakeholder management, and data-driven decision making. The role is hybrid with 2-3 days in office. Responsibilities include product roadmap planning, cross-functional collaboration, user research, and driving product metrics. We're a mid-size SaaS company with a results-oriented culture. Budget is ₹15-22 lakhs." 
    },
    { 
      icon: DollarSign, 
      label: "Sales", 
      prompt: "We need a Sales Manager with 5-7 years experience and proven track record in B2B sales, team leadership, and CRM tools (Salesforce, HubSpot). The role is office-based with travel requirements (30% travel). Responsibilities include managing sales team, developing sales strategies, client relationship management, and achieving revenue targets. We're an established enterprise software company with a performance-driven culture. Budget is ₹10-16 lakhs." 
    },
  ];

  const handleUseExample = () => {
    setPrompt(examplePrompt);
    setCopiedExample(true);
    setTimeout(() => setCopiedExample(false), 2000);
  };

  const handleQuickPrompt = (quickPrompt) => {
    setPrompt(quickPrompt);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) {
      setError('Please enter a prompt to generate job description');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      // Debug log removed — was leaking user prompt to console.

      // Try both localhost and production URLs
      const urls = getApiUrlsWithFallback('/api/generate_jd_ai');

      let response;
      let lastError;

      // Company context is REQUIRED by the backend (company_id). In the Company
      // Admin panel the target company arrives as ?companyID=; on the employer
      // side it lives in localStorage. Without it the API returns 400.
      const companyIDParam = new URLSearchParams(location.search).get('companyID');
      const company_id = companyIDParam || localStorage.getItem('companyAccountId') || undefined;
      const company_name = localStorage.getItem('company_name') || undefined;
      const company_email = localStorage.getItem('employer_email') || undefined;

      for (const url of urls) {
        try {
          // Debug log removed — was leaking API URL to console.
          response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ prompt, company_id, company_name, company_email })
          });

          if (response.ok) {
            // Debug log removed — was leaking API URL to console.
            break;
          }
        } catch (err) {
          // Debug log removed — was leaking API URL to console.
          lastError = err;
          continue;
        }
      }

      if (!response) {
        throw lastError || new Error('Failed to connect to API servers');
      }

      // Debug logs removed — were leaking response status/headers to console.

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        console.error('API Error:', response.status);
        const err = new Error(`Failed to generate job description: ${response.status}`);
        // Plan/limit refusals carry a customer-ready sentence; show it as is.
        if ([402, 403].includes(response.status) && body?.error) err.userMessage = body.error;
        throw err;
      }

      const data = await response.json();
      // Debug log removed — was leaking AI response payload to console.

      const generatedJdData = data.job_details;
      // Debug log removed — was leaking generated job description to console.

      // Store the generated data in localStorage and navigate to JD creation page
      localStorage.setItem('generatedJdData', JSON.stringify(generatedJdData));
      // Debug log removed — was state-machine noise.

      setIsGenerating(false);
      const companyID = new URLSearchParams(location.search).get('companyID');
      // Decide target based on context: if embedded (Company Admin) or companyID present -> admin route; else -> HireWithUs route
      const target = (embedded || companyID)
        ? (companyID ? `/create-JD?companyID=${encodeURIComponent(companyID)}` : '/create-JD')
        : '/hirewithus/postjob';
      // Debug log removed — was state-machine noise.
      navigate(target);
    } catch (error) {
      console.error('JD Generation Error:', error);
      setError(error.userMessage || 'Failed to generate job description. Please try again.');
      setIsGenerating(false);
    }
  };

  const Inner = (
    <div className="flex flex-col w-full gap-6">
      {/* Back Navigation */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/hirewithus/postjob')}
        className="w-fit"
      >
        <ChevronLeft className="h-4 w-4 mr-1" />
        Back to Post Job Options
      </Button>

      <div className="flex flex-col w-full gap-6">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <div className="p-1.5 rounded-md bg-primary/10">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <Heading level="h4">Generate Job Description with AI</Heading>
          </div>
          <Text className="text-sm text-muted-foreground">
            Describe your ideal candidate and let AI create a comprehensive job description
          </Text>
        </div>

        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Quick Prompt Buttons */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Quick Start Templates
                </Label>
                <div className="flex flex-wrap gap-2">
                  {quickPrompts.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <Button
                        key={idx}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickPrompt(item.prompt)}
                        disabled={isGenerating}
                        className="gap-1.5 h-8 text-xs"
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {item.label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Main Textarea */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="prompt" className="text-base">
                    Job Description Details
                  </Label>
                  <Text variant="muted" size="xs" className="text-xs tabular-nums">
                    {prompt.length}
                  </Text>
                </div>
                <Textarea
                  ref={textareaRef}
                  id="prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe the job position you want to create. Include details like role, experience level, skills, work arrangement, company culture, and budget..."
                  rows={6}
                  className="min-h-[140px] resize-y text-sm leading-relaxed"
                  disabled={isGenerating}
                />
                <div className="flex items-start gap-1.5 -mb-1">
                  <Lightbulb className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <Text variant="muted" size="xs" className="text-xs leading-relaxed">
                    Be specific about requirements, skills, and company culture for better results
                  </Text>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription className="text-sm">{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-center">
                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  disabled={isGenerating || !prompt.trim()}
                  className="min-w-[220px] gap-2"
                  style={{
                    backgroundColor: getColor('primary'),
                    color: getColor('primaryForeground'),
                    borderColor: getColor('primary')
                  }}
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4" />
                      Generate Job Description
                    </>
                  )}
                </Button>
              </div>
            </form>

            {/* Loading State */}
            {isGenerating && (
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="pt-6 pb-6">
                  <div className="flex flex-col items-center justify-center text-center space-y-4">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <div className="space-y-1.5">
                      <h3 className="text-base font-semibold text-foreground">
                        Generating your job description...
                      </h3>
                      <Text variant="muted" size="sm" className="max-w-sm">
                        Our AI is analyzing your requirements. This may take a few moments.
                      </Text>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Badge variant="secondary" className="gap-1.5 text-xs">
                        <CheckCircle2 className="h-3 w-3" />
                        Analyzing
                      </Badge>
                      <Badge variant="secondary" className="gap-1.5 text-xs">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Generating
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>

        {/* Tips & Example Section */}
        <Collapsible open={tipsOpen} onOpenChange={setTipsOpen}>
          <Card className="border-l-2 border-l-primary">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-md bg-primary/10">
                      <Lightbulb className="h-4 w-4 text-primary" />
                    </div>
                    <div className="text-left">
                      <CardTitle className="text-base font-semibold">Writing Tips & Best Practices</CardTitle>
                      <CardDescription className="text-xs">Learn how to write effective prompts</CardDescription>
                    </div>
                  </div>
                  <ChevronDown className={cn(
                    "h-4 w-4 text-muted-foreground transition-transform duration-200",
                    tipsOpen && "transform rotate-180"
                  )} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <CardContent className="pt-0 space-y-4">
                {/* Tips Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {[
                    { icon: Briefcase, title: "Job Title & Level", desc: "Include specific role (e.g., 'Senior Frontend Developer', 'Junior Data Scientist')" },
                    { icon: Zap, title: "Required Skills", desc: "Mention specific technologies (e.g., 'React, Python, AWS, SQL')" },
                    { icon: MapPin, title: "Work Arrangement", desc: "Specify if remote, hybrid, or office-based" },
                    { icon: Clock, title: "Experience Level", desc: "Include years of experience needed (e.g., '3-5 years experience')" },
                    { icon: DollarSign, title: "Salary Expectations", desc: "Mention budget range if known" },
                    { icon: Users, title: "Company Details", desc: "Describe company culture, size, industry" },
                  ].map((tip, idx) => {
                    const Icon = tip.icon;
                    return (
                      <div key={idx} className="flex gap-2.5 p-2.5 rounded-md bg-muted/40 hover:bg-muted/60 transition-colors border border-transparent hover:border-border">
                        <Icon className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0 space-y-0.5">
                          <Text size="xs" weight="semibold" className="leading-tight">{tip.title}</Text>
                          <Text size="xs" variant="muted" className="leading-relaxed">{tip.desc}</Text>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Example Prompt */}
                <div className="pt-3 border-t">
                  <div className="flex items-start justify-between gap-3 mb-2.5">
                    <div className="flex items-center gap-1.5">
                      <Lightbulb className="h-3.5 w-3.5 text-primary" />
                      <h4 className="text-sm font-semibold text-foreground">
                        Example Prompt
                      </h4>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleUseExample}
                      disabled={isGenerating}
                      className="gap-1.5 h-7 text-xs"
                    >
                      {copiedExample ? (
                        <>
                          <CheckCircle2 className="h-3 w-3" />
                          Used
                        </>
                      ) : (
                        <>
                          <Copy className="h-3 w-3" />
                          Use This
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="p-3 bg-muted/40 rounded-md border-l-2 border-l-primary">
                    <Text
                      variant="muted"
                      className="text-xs leading-relaxed italic"
                    >
                      "{examplePrompt}"
                    </Text>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
    </div>
  );

  if (embedded) {
    return Inner;
  }

  return (
    <EmployerLayout>
      {Inner}
    </EmployerLayout>
  );
}

export default JdAi;